import React, { useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { toast } from 'react-hot-toast';
import {
  ArrowLeft,
  Edit,
  Trash2,
  CheckCircle,
  XCircle,
  Ban,
  Car,
  Building,
  User,
  MapPin,
  FileText,
  Download,
  History,
  Building2,
  Calendar,
  Fuel,
  Settings,
  Navigation,
  Eye,
  X,
  Phone,
  Mail,
  Shield,
  Star,
  LogOut
} from 'lucide-react';
import { api } from '../../utils/api';
import { useAuth } from '../../contexts/AuthContext';
import BlockVehicleModal from '../../components/BlockVehicleModal';
import VehicleDocumentViewer from '../../components/VehicleDocumentViewer';
import VehicleHistoryModal from '../../components/VehicleHistoryModal';
import DriverList from './DriverList';

const VehicleDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { hasPermission, user } = useAuth();

  // Get query parameters to check if we came from driver details
  const searchParams = new URLSearchParams(window.location.search);
  const fromDriver = searchParams.get('from') === 'driver';
  const driverId = searchParams.get('driverId');
  const originalSource = searchParams.get('originalSource'); // Preserve original navigation source
  const queryClient = useQueryClient();
  const [showBlockModal, setShowBlockModal] = useState(false);
  const [showDocumentViewer, setShowDocumentViewer] = useState(false);
  const [showHistoryModal, setShowHistoryModal] = useState(false);
  const [showDriverList, setShowDriverList] = useState(false);

  const { data, isLoading, error, refetch } = useQuery(
    ['vehicle', id],
    () => api.get(`/vehicles/${id}`),
    {
      enabled: !!id
    }
  );

  // Extract vehicle data
  const vehicleData = data?.data?.data || data?.data;
  const vehicle = vehicleData?.vehicle || vehicleData;



  // Use populated data from vehicle instead of separate API calls
  const vendorData = vehicle?.vendorId;
  const regionalOfficeData = vehicle?.regionalOffice;

  // Fetch assigned driver if vehicle has one
  const { data: assignedDriverData } = useQuery(
    ['driver', vehicle?.driverId],
    () => api.get(`/drivers/${vehicle.driverId}`),
    {
      enabled: !!vehicle?.driverId && vehicle.driverId !== "",
      select: (data) => data?.data?.data?.driver || data?.data?.driver
    }
  );

  const approveMutation = useMutation(
    () => api.patch(`/vehicles/${id}/approve`, { approvalReason: 'Approved by admin' }),
    {
      onSuccess: () => {
        toast.success('Vehicle approved successfully');
        refetch();
      },
      onError: (error) => {
        toast.error(error?.response?.data?.message || 'Failed to approve vehicle');
      }
    }
  );

  const rejectMutation = useMutation(
    () => api.patch(`/vehicles/${id}/reject`, { rejectionReason: 'Rejected by admin' }),
    {
      onSuccess: () => {
        toast.success('Vehicle rejected successfully');
        refetch();
      },
      onError: (error) => {
        toast.error(error?.response?.data?.message || 'Failed to reject vehicle');
      }
    }
  );

  const blockMutation = useMutation(
    (blockData) => api.patch(`/vehicles/${blockData.vehicleId}/block`, blockData),
    {
      onSuccess: () => {
        toast.success('Vehicle status updated successfully');
        refetch();
        setShowBlockModal(false);
      },
      onError: (error) => {
        toast.error(error?.response?.data?.message || 'Failed to update vehicle status');
      }
    }
  );

  const deleteMutation = useMutation(
    () => api.delete(`/vehicles/${id}`),
    {
      onSuccess: () => {
        toast.success('Vehicle deleted successfully');
        navigate('/vehicles');
      },
      onError: (error) => {
        toast.error(error?.response?.data?.message || 'Failed to delete vehicle');
      }
    }
  );

  const toggleActiveMutation = useMutation(
    (isActive) => api.patch(`/vehicles/${id}/toggle-active`, { isActive }),
    {
      onSuccess: () => {
        toast.success('Vehicle status updated successfully');
        refetch();
      },
      onError: (error) => {
        toast.error(error?.response?.data?.message || 'Failed to update vehicle status');
      }
    }
  );

  const logoutMutation = useMutation(
    () => api.post(`/vehicles/${id}/logout-driver`),
    {
      onSuccess: () => {
        toast.success('Driver logged out and vehicle assignment removed successfully');
        refetch();
        queryClient.invalidateQueries(['driver', vehicle?.driverId]);
      },
      onError: (error) => {
        toast.error(error?.response?.data?.message || 'Failed to logout driver');
      }
    }
  );

  const handleApprove = () => {
    if (window.confirm('Are you sure you want to approve this vehicle?')) {
      approveMutation.mutate();
    }
  };

  const handleReject = () => {
    if (window.confirm('Are you sure you want to reject this vehicle?')) {
      rejectMutation.mutate();
    }
  };

  const handleBlock = (blockData) => {
    blockMutation.mutate(blockData);
  };

  const handleLogout = () => {
    if (window.confirm('Are you sure you want to log out the assigned driver from this vehicle?')) {
      logoutMutation.mutate();
    }
  };

  const assignDriverMutation = useMutation(
    (driverId) => api.post(`/vehicles/${id}/assign-driver`, { driverId }),
    {
      onSuccess: () => {
        toast.success('Driver assigned successfully');
        setShowDriverList(false);
        refetch();
      },
      onError: (error) => {
        toast.error(error?.response?.data?.message || 'Failed to assign driver');
      }
    }
  );

  const handleAssignDriver = (driver) => {
    if (window.confirm(`Are you sure you want to assign driver "${driver.name}" to this vehicle?`)) {
      assignDriverMutation.mutate(driver._id);
    }
  };

  const handleDelete = () => {
    if (window.confirm('Are you sure you want to delete this vehicle?')) {
      deleteMutation.mutate();
    }
  };

  const handleToggleActive = () => {
    const newStatus = !vehicle.isActive;
    if (window.confirm(`Are you sure you want to ${newStatus ? 'activate' : 'deactivate'} this vehicle?`)) {
      toggleActiveMutation.mutate(newStatus);
    }
  };

  const getStatusBadge = () => {
    if (vehicle?.isDeleted) return <span className="badge badge-error">Deleted</span>;
    if (vehicle?.isBlocked) return <span className="badge badge-error">Blocked</span>;
    if (!vehicle?.isActive) return <span className="badge badge-warning">Inactive</span>;
    if (vehicle?.isApproved) return <span className="badge badge-success">Approved</span>;
    return <span className="badge badge-warning">Pending</span>;
  };

  const formatExpiryDate = (timestamp) => {
    if (!timestamp) return 'N/A';
    const ms = timestamp < 1e12 ? timestamp * 1000 : timestamp;
    const date = new Date(ms);
    return date.toLocaleDateString();
  };

  const getExpiryStatus = (timestamp) => {
    if (!timestamp) return 'text-gray-500';
    const ms = timestamp < 1e12 ? timestamp * 1000 : timestamp;
    const date = new Date(ms);
    const now = new Date();
    const daysUntilExpiry = Math.ceil((date - now) / (1000 * 60 * 60 * 24));

    if (daysUntilExpiry < 0) return 'text-red-500';
    if (daysUntilExpiry <= 30) return 'text-yellow-500';
    return 'text-green-500';
  };

  if (isLoading) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-center py-8">
          <div className="loading-spinner"></div>
        </div>
      </div>
    );
  }

  if (error || !vehicle) {
    return (
      <div className="space-y-6">
        <div className="text-center py-8">
          <Car className="h-12 w-12 text-gray-400 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Vehicle Not Found</h2>
          <button onClick={() => navigate('/vehicles')} className="btn btn-primary">
            Back to Vehicles
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="page-header">
        <div className="px-4 py-5 sm:px-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <button
                onClick={() => {
                  if (fromDriver && driverId) {
                    // Preserve original navigation context when going back to driver details
                    const driverUrl = originalSource
                      ? `/drivers/${driverId}?from=${originalSource}&sourceId=${driverId}`
                      : `/drivers/${driverId}`;
                    navigate(driverUrl);
                  } else {
                    navigate('/vehicles');
                  }
                }}
                className="text-gray-400 hover:text-gray-600"
              >
                <ArrowLeft className="h-6 w-6" />
              </button>
              <div>
                <h1 className="page-title">{vehicle.make} {vehicle.model}</h1>
                <p className="page-subtitle">Vehicle Details</p>
              </div>
            </div>
            <div className="flex items-center space-x-2">
              <button
                onClick={() => setShowHistoryModal(true)}
                className="btn btn-secondary"
              >
                <History className="h-4 w-4 mr-2" />
                History
              </button>

              <button
                onClick={() => setShowDocumentViewer(true)}
                className="btn btn-secondary"
              >
                <FileText className="h-4 w-4 mr-2" />
                Documents
              </button>

              {vehicle.location && (
                <Link
                  to={`/vehicles/${vehicle._id}/location`}
                  className="btn btn-info"
                >
                  <Navigation className="h-4 w-4 mr-2" />
                  Track Location
                </Link>
              )}

              {hasPermission('vehicles', 'write') && (
                <Link
                  to={`/vehicles/${vehicle._id}/edit`}
                  className="btn btn-secondary"
                >
                  <Edit className="h-4 w-4 mr-2" />
                  Edit Vehicle
                </Link>
              )}

              {hasPermission('vehicles', 'write') && !vehicle.isApproved && (
                <button
                  onClick={handleApprove}
                  disabled={approveMutation.isLoading}
                  className="btn btn-success"
                >
                  <CheckCircle className="h-4 w-4 mr-2" />
                  Approve
                </button>
              )}

              {hasPermission('vehicles', 'write') && (
                <button
                  onClick={() => setShowBlockModal(true)}
                  disabled={blockMutation.isLoading}
                  className={`btn ${vehicle.isBlocked ? 'btn-success' : 'btn-warning'}`}
                >
                  {vehicle.isBlocked ? (
                    <>
                      <CheckCircle className="h-4 w-4 mr-2" />
                      Unblock
                    </>
                  ) : (
                    <>
                      <XCircle className="h-4 w-4 mr-2" />
                      Block
                    </>
                  )}
                </button>
              )}

              {hasPermission('vehicles', 'write') && (
                <button
                  onClick={handleToggleActive}
                  disabled={toggleActiveMutation.isLoading}
                  className={`btn ${vehicle.isActive ? 'btn-warning' : 'btn-success'}`}
                >
                  {vehicle.isActive ? (
                    <>
                      <XCircle className="h-4 w-4 mr-2" />
                      Deactivate
                    </>
                  ) : (
                    <>
                      <CheckCircle className="h-4 w-4 mr-2" />
                      Activate
                    </>
                  )}
                </button>
              )}

              {hasPermission('vehicles', 'delete') && (
                <button
                  onClick={handleDelete}
                  disabled={deleteMutation.isLoading}
                  className="btn btn-danger"
                >
                  <Trash2 className="h-4 w-4 mr-2" />
                  Delete
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Vehicle Information */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Basic Information */}
        <div className="card">
          <div className="card-header">
            <h3 className="text-lg font-medium text-gray-900">Basic Information</h3>
          </div>
          <div className="card-body space-y-4">
            <div className="flex items-center space-x-3">
              <div className="h-16 w-16 rounded-full bg-primary-100 flex items-center justify-center">
                <Car className="h-8 w-8 text-primary-700" />
              </div>
              <div>
                <h4 className="text-lg font-medium text-gray-900">{vehicle.make} {vehicle.model}</h4>
                <p className="text-sm text-gray-500">{vehicle.regNo}</p>
                {getStatusBadge()}
              </div>
            </div>

            <div className="space-y-3">
              <div className="flex items-center space-x-3">
                <Settings className="h-4 w-4 text-gray-400" />
                <span className="text-sm text-gray-900">Fleet ID: {vehicle.fleetSysId}</span>
              </div>

              <div className="flex items-center space-x-3">
                <Calendar className="h-4 w-4 text-gray-400" />
                <span className="text-sm text-gray-900">Year: {vehicle.year}</span>
              </div>

              <div className="flex items-center space-x-3">
                <Car className="h-4 w-4 text-gray-400" />
                <span className="text-sm text-gray-900">Color: {vehicle.color}</span>
              </div>

              <div className="flex items-center space-x-3">
                <Fuel className="h-4 w-4 text-gray-400" />
                <span className="text-sm text-gray-900">Fuel: {vehicle.fuel}</span>
              </div>

              <div className="flex items-center space-x-3">
                <Settings className="h-4 w-4 text-gray-400" />
                <span className="text-sm text-gray-900">Type: {vehicle.type}</span>
              </div>

              {vehicle.permitNumber && (
                <div className="flex items-center space-x-3">
                  <FileText className="h-4 w-4 text-gray-400" />
                  <span className="text-sm text-gray-900">Permit: {vehicle.permitNumber}</span>
                </div>
              )}

              {/* Regional Office Information */}
              {regionalOfficeData && (
                <div className="flex items-center space-x-3">
                  <Building2 className="h-4 w-4 text-gray-400" />
                  <Link
                    to={`/regional-offices/${regionalOfficeData._id}`}
                    className="text-sm text-primary-600 hover:text-primary-900"
                  >
                    Regional Office: {regionalOfficeData.name} ({regionalOfficeData.city}, {regionalOfficeData.state})
                  </Link>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Performance Stats */}
        <div className="card">
          <div className="card-header">
            <h3 className="text-lg font-medium text-gray-900">Performance</h3>
          </div>
          <div className="card-body space-y-4">
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-600">Total Trips</span>
              <span className="text-sm font-medium text-gray-900">
                {vehicle.totalTrips || 0}
              </span>
            </div>

            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-600">Total Earnings</span>
              <span className="text-sm font-medium text-gray-900">
                ₹{vehicle.totalEarnings?.toFixed(2) || '0.00'}
              </span>
            </div>

            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-600">Mileage</span>
              <span className="text-sm font-medium text-gray-900">
                {vehicle.mileage || 0} km
              </span>
            </div>

            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-600">Status</span>
              <span className="text-sm font-medium text-gray-900">
                {vehicle.isActive ? 'Active' : 'Inactive'}
              </span>
            </div>
          </div>
        </div>

        {/* Vendor Information */}
        <div className="card">
          <div className="card-header">
            <h3 className="text-lg font-medium text-gray-900">Vendor</h3>
          </div>
          <div className="card-body space-y-4">
            {vendorData ? (
              <>
                <div className="flex items-center space-x-3">
                  <Building className="h-4 w-4 text-gray-400" />
                  <Link
                    to={`/vendors/${vendorData._id}`}
                    className="text-sm text-primary-600 hover:text-primary-900"
                  >
                    {vendorData.VendorName}
                  </Link>
                </div>

                <div className="space-y-2">
                  <div className="flex justify-between">
                    <span className="text-sm text-gray-600">Fleet ID</span>
                    <span className="text-sm text-gray-900">{vendorData.fleetSysId}</span>
                  </div>

                  <div className="flex justify-between">
                    <span className="text-sm text-gray-600">Owner</span>
                    <span className="text-sm text-gray-900">{vendorData.OwnerName}</span>
                  </div>

                  <div className="flex justify-between">
                    <span className="text-sm text-gray-600">Phone</span>
                    <span className="text-sm text-gray-900">{vendorData.ownerPhone}</span>
                  </div>

                  <div className="flex justify-between">
                    <span className="text-sm text-gray-600">Email</span>
                    <span className="text-sm text-gray-900">{vendorData.ownerEmail}</span>
                  </div>
                </div>
              </>
            ) : (
              <div className="text-center py-4">
                <Building className="h-8 w-8 text-gray-400 mx-auto mb-2" />
                <p className="text-sm text-gray-500">No vendor assigned</p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Assigned Driver */}
      {assignedDriverData ? (
        <div className="card">
          <div className="card-header flex justify-between items-center">
            <h3 className="text-lg font-medium text-gray-900">Assigned Driver</h3>
            {hasPermission('vehicles', 'write') && (
              <button
                onClick={handleLogout}
                disabled={logoutMutation.isLoading}
                className="btn btn-warning btn-sm"
              >
                <LogOut className="h-4 w-4 mr-2" />
                Logout Driver
              </button>
            )}
          </div>
          <div className="card-body space-y-4">
            <div className="flex items-center space-x-3">
              <div className="h-12 w-12 rounded-full bg-primary-100 flex items-center justify-center">
                <User className="h-6 w-6 text-primary-700" />
              </div>
              <div>
                <Link
                  to={`/drivers/${assignedDriverData._id}`}
                  className="text-lg font-medium text-primary-600 hover:text-primary-900"
                >
                  {assignedDriverData.name}
                </Link>
                <p className="text-sm text-gray-500">{assignedDriverData.licenseNo}</p>
              </div>
            </div>

            <div className="space-y-3">
              <div className="flex items-center space-x-3">
                <Phone className="h-4 w-4 text-gray-400" />
                <span className="text-sm text-gray-900">{assignedDriverData.phone}</span>
              </div>

              {assignedDriverData.email && (
                <div className="flex items-center space-x-3">
                  <Mail className="h-4 w-4 text-gray-400" />
                  <span className="text-sm text-gray-900">{assignedDriverData.email}</span>
                </div>
              )}

              <div className="flex items-center space-x-3">
                <Calendar className="h-4 w-4 text-gray-400" />
                <span className="text-sm text-gray-900">
                  Joined {new Date(assignedDriverData.createdOn).toLocaleDateString()}
                </span>
              </div>

              <div className="flex items-center space-x-3">
                <Shield className="h-4 w-4 text-gray-400" />
                <span className="text-sm text-gray-900">
                  Status: {assignedDriverData.isApproved ? 'Approved' : 'Pending'}
                </span>
              </div>

              {assignedDriverData.ratingData && (
                <div className="flex items-center space-x-3">
                  <Star className="h-4 w-4 text-gray-400" />
                  <span className="text-sm text-gray-900">
                    Rating: {assignedDriverData.ratingData.currentrating?.toFixed(1) || '0.0'}
                    ({assignedDriverData.ratingData.count || 0} reviews)
                  </span>
                </div>
              )}

              {assignedDriverData.totalTripsAccepted && (
                <div className="flex items-center space-x-3">
                  <MapPin className="h-4 w-4 text-gray-400" />
                  <span className="text-sm text-gray-900">
                    Trips: {assignedDriverData.totalTripsAccepted || 0} accepted, {assignedDriverData.totalTripsRejected || 0} rejected
                  </span>
                </div>
              )}

              {assignedDriverData.driverEarnings && (
                <div className="flex items-center space-x-3">
                  <span className="text-sm text-gray-400">₹</span>
                  <span className="text-sm text-gray-900">
                    Earnings: ₹{assignedDriverData.driverEarnings?.toFixed(2) || '0.00'}
                  </span>
                </div>
              )}
            </div>
          </div>
        </div>
      ) : (
        <div className="card">
          <div className="card-header flex justify-between items-center">
            <h3 className="text-lg font-medium text-gray-900">Assigned Driver</h3>
            {hasPermission('vehicles', 'write') && (
              <button
                onClick={() => setShowDriverList(true)}
                className="btn btn-success btn-sm"
              >
                <User className="h-4 w-4 mr-2" />
                Assign Driver
              </button>
            )}
          </div>
          <div className="card-body">
            <div className="text-center py-8">
              <User className="h-12 w-12 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">No Driver Assigned</h3>
              <p className="text-gray-500">This vehicle is not currently assigned to any driver.</p>
            </div>
          </div>
        </div>
      )}

      {/* Previous Drivers History */}
      <div className="card mt-6">
        <div className="card-header flex items-center space-x-2">
          <History className="h-5 w-5 text-gray-500" />
          <h3 className="text-lg font-medium text-gray-900">Previous Drivers History</h3>
        </div>
        <div className="card-body">
          {!vehicle?.previousDrivers || vehicle.previousDrivers.length === 0 ? (
            <p className="text-gray-500 text-sm text-center py-4">No previous drivers recorded for this vehicle.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="table w-full text-sm">
                <thead>
                  <tr className="border-b border-gray-200 text-left text-gray-500">
                    <th className="pb-2 font-medium">Driver Name</th>
                    <th className="pb-2 font-medium">Phone</th>
                    <th className="pb-2 font-medium">Email</th>
                    <th className="pb-2 font-medium">License No</th>
                    <th className="pb-2 font-medium">Logout Date & Time</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {vehicle.previousDrivers.map((record, index) => {
                    const driver = record.driverId; // populated as object
                    return (
                      <tr key={record._id || index} className="text-gray-700">
                        <td className="py-3 font-medium text-gray-950">
                          {driver ? (
                            <Link to={`/drivers/${driver._id}`} className="text-primary-600 hover:text-primary-900">
                              {driver.name}
                            </Link>
                          ) : (
                            <span className="text-gray-400 italic">Unknown Driver ({record.driverId || 'N/A'})</span>
                          )}
                        </td>
                        <td className="py-3">{driver?.phone || 'N/A'}</td>
                        <td className="py-3">{driver?.email || 'N/A'}</td>
                        <td className="py-3">{driver?.licenseNo || 'N/A'}</td>
                        <td className="py-3 text-gray-500">
                          {record.LogoutDate ? new Date(record.LogoutDate).toLocaleString() : 'N/A'}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Document Expiry Dates */}
      <div className="card">
        <div className="card-header flex items-center justify-between">
          <h3 className="text-lg font-medium text-gray-900">Document Expiry Dates</h3>
          {vehicle.isParivahanFailed === true && (
            <div
              className={`text-sm bg-red-300 border-none cursor-pointer rounded-full w-max px-3 py-1 text-red-900 font-medium inline-block`}
            >
              Parivahan Failed
            </div>
          )}
          {vehicle.isParivahanFailed === false && (
            <div
              className={`text-sm bg-green-300 border-none cursor-pointer rounded-full w-max px-3 py-1 text-green-900 font-medium inline-block`}
            >
              Parivahan Verified
            </div>
          )}
        </div>
        <div className="card-body">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="space-y-2">
              <div className="flex justify-between">
                <span className="text-sm text-gray-600">Insurance</span>
                <span className={`text-sm font-medium ${getExpiryStatus(vehicle.insuranceExpiry)}`}>
                  {formatExpiryDate(vehicle.insuranceExpiry)}
                </span>
              </div>
            </div>

            <div className="space-y-2">
              <div className="flex justify-between">
                <span className="text-sm text-gray-600">Fitness</span>
                <span className={`text-sm font-medium ${getExpiryStatus(vehicle.fitnessExpiry)}`}>
                  {formatExpiryDate(vehicle.fitnessExpiry)}
                </span>
              </div>
            </div>

            <div className="space-y-2">
              <div className="flex justify-between">
                <span className="text-sm text-gray-600">PUC</span>
                <span className={`text-sm font-medium ${getExpiryStatus(vehicle.pucExpiry)}`}>
                  {formatExpiryDate(vehicle.pucExpiry)}
                </span>
              </div>
            </div>

            <div className="space-y-2">
              <div className="flex justify-between">
                <span className="text-sm text-gray-600">Road Tax</span>
                <span className={`text-sm font-medium ${getExpiryStatus(vehicle.roadTaxExpiry)}`}>
                  {formatExpiryDate(vehicle.roadTaxExpiry)}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Block Vehicle Modal */}
      <BlockVehicleModal
        isOpen={showBlockModal}
        onClose={() => setShowBlockModal(false)}
        vehicle={vehicle}
        onBlock={handleBlock}
      />

      {/* Document Viewer Modal */}
      <VehicleDocumentViewer
        isOpen={showDocumentViewer}
        onClose={() => setShowDocumentViewer(false)}
        vehicle={vehicle}
      />

      {/* Vehicle History Modal */}
      <VehicleHistoryModal
        isOpen={showHistoryModal}
        onClose={() => setShowHistoryModal(false)}
        vehicleId={vehicle?._id}
        vehicleName={`${vehicle?.make} ${vehicle?.model} (${vehicle?.regNo})`}
      />

      {/* Driver List Modal */}
      <DriverList
        isOpen={showDriverList}
        onClose={() => setShowDriverList(false)}
        unassignedOnly={true}
        onSelectDriver={handleAssignDriver}
      />
    </div>
  );
};

export default VehicleDetails; 